'use client';

import React, { useState, useEffect, useCallback } from 'react';
import FilterPanel from './FilterPanel';
import ListingsGrid from './ListingsGrid';
import FeedHeader from './FeedHeader';
import { categories, conditions, regions, type Listing } from './feedData';
import { createClient } from '@/lib/supabase/client';
import { useSearchParams } from 'next/navigation';

export type SortOption = 'newest' | 'price-asc' | 'price-desc';

export type Filters = {
  categories: string[];
  conditions: string[];
  regions: string[];
  priceMin: string;
  priceMax: string;
};

const defaultFilters: Filters = {
  categories: [],
  conditions: [],
  regions: [],
  priceMin: '',
  priceMax: '',
};

export default function ListingsFeedContent() {
  const searchParams = useSearchParams();
  const [filters, setFilters] = useState<Filters>(() => ({
    ...defaultFilters,
    categories: searchParams.get('cat') ? [searchParams.get('cat')!] : [],
  }));
  const [sort, setSort] = useState<SortOption>('newest');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = createClient();
      let query = supabase
        .from('listings')
        .select('*, seller:user_profiles(id, full_name, store_name, rating, review_count)', { count: 'exact' })
        .eq('listing_status', 'active');

      if (searchQuery.trim()) {
        query = query.or(`title.ilike.%${searchQuery.trim()}%,description.ilike.%${searchQuery.trim()}%,category.ilike.%${searchQuery.trim()}%`);
      }
      if (filters.categories.length > 0) {
        query = query.in('category_slug', filters.categories);
      }
      if (filters.conditions.length > 0) {
        query = query.in('condition', filters.conditions);
      }
      if (filters.regions.length > 0) {
        query = query.in('region', filters.regions);
      }
      if (filters.priceMin) {
        query = query.gte('price', parseFloat(filters.priceMin));
      }
      if (filters.priceMax) {
        query = query.lte('price', parseFloat(filters.priceMax));
      }

      switch (sort) {
        case 'price-asc':
          query = query.order('price', { ascending: true });
          break;
        case 'price-desc':
          query = query.order('price', { ascending: false });
          break;
        default:
          query = query.order('created_at', { ascending: false });
      }

      const { data, error, count } = await query;
      if (!error) {
        setListings((data as unknown as Listing[]) || []);
        setTotalCount(count || 0);
      }
    } catch {}
    setLoading(false);
  }, [filters, sort, searchQuery]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const activeFilterCount =
    filters.categories.length +
    filters.conditions.length +
    filters.regions.length +
    (filters.priceMin ? 1 : 0) +
    (filters.priceMax ? 1 : 0);

  function handleResetFilters() {
    setFilters(defaultFilters);
    setSearchQuery('');
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-8">
      <FeedHeader
        resultCount={totalCount}
        sort={sort}
        onSortChange={setSort}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activeFilterCount={activeFilterCount}
        onToggleFilters={() => setFilterPanelOpen(!filterPanelOpen)}
      />

      <div className="flex gap-6 mt-6">
        {/* Desktop filter panel */}
        <aside className="hidden lg:block w-60 xl:w-64 shrink-0">
          <FilterPanel
            filters={filters}
            onChange={setFilters}
            onReset={handleResetFilters}
            activeCount={activeFilterCount}
          />
        </aside>

        {/* Mobile filter drawer */}
        {filterPanelOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setFilterPanelOpen(false)} />
            <div className="absolute top-0 left-0 h-full w-80 bg-[hsl(222,40%,9%)] border-r border-[hsl(222,25%,18%)] overflow-y-auto p-4">
              <FilterPanel
                filters={filters}
                onChange={setFilters}
                onReset={handleResetFilters}
                activeCount={activeFilterCount}
                onClose={() => setFilterPanelOpen(false)}
              />
            </div>
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 min-w-0">
          <ListingsGrid listings={listings} loading={loading} onReset={handleResetFilters} hasFilters={activeFilterCount > 0 || !!searchQuery} />
        </div>
      </div>
    </div>
  );
}